import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { differenceInSeconds } from 'date-fns';
import styles from './styles/EvCharging.module.scss';

// Context.
import { OmaLummeApp, EvChargingApp } from '../../omaLummeContext';

// Components.
import Box from '../Common/Box';
import Button from '../Common/Button';
import Liftup from '../Common/Liftup';
import Loading from '../Common/Loading';
import Select from '../Common/Select';
import ChargerDropdown from './ChargerDropdown';
import Battery from './Battery';
import Snackbar from '../Common/Snackbar';

// Actions.
import { fetchEvChargingStatus, startEvCharging, cancelStartEvCharging, stopEvCharging, fetchEVChargerInfo } from '../../actions';
// Types.
import { MeteringPointOption } from '../../types/consumptionRates';
import { EmablerTariffElementUI, EvChargingStatus } from '../../types/evCharging';
// Utils.
import { roundNumber, setPriceFormat } from '../../utils/billing';
import { useInterval } from '../../utils/interval';
import { contractIsEvCharging, formatDays, formatTag, formatWatts, isCharging, renderHours } from '../../utils/evCharging';

const EvCharging: React.FunctionComponent = () => {
  const dispatchEV = EvChargingApp.useDispatch();
  const { t } = useTranslation();

  const { config, users, basicData, loadingBasicData, selectedMeteringPointContract } = OmaLummeApp.useState();
  const { evChargerInfo, evChargerInfoLoading, selectedCharger, chargerStatus, activeTag, activeSite } = EvChargingApp.useState();

  const [statusInterval, setStatusInterval] = React.useState<number | null>(null);

  const [startingCharging, setStartingCharging] = React.useState<boolean>(false);
  const [stoppingCharging, setStoppingCharging] = React.useState<boolean>(false);
  const [startingError, setStartingError] = React.useState<boolean>(false);
  const [chargingError, setChargingError] = React.useState<boolean>(false);

  const [tempActiveTag, setTempActiveTag] = React.useState<string>(activeTag);
  const [tempActiveSite, setTempActiveSite] = React.useState<string>(activeSite);

  React.useEffect(() => {
    if (activeTag !== tempActiveTag) {
      dispatchEV(['changeActiveTag', tempActiveTag]);
      window.localStorage.setItem('EvChargingTag', tempActiveTag);
    }
  }, [tempActiveTag]);

  React.useEffect(() => {
    if (activeSite !== tempActiveSite) {
      dispatchEV(['changeActiveSite', tempActiveSite]);
      window.localStorage.setItem('EvChargingSite', tempActiveSite);
    }
  }, [tempActiveSite]);

  const tags: MeteringPointOption[] = evChargerInfo
    ? evChargerInfo.tags.map((tag) => {
        return { value: tag.idTag, label: `Lataustunniste: ${formatTag(tag.idTag)} (${tag.tokenName})` };
      })
    : [];
  const sites: MeteringPointOption[] = evChargerInfo
    ? evChargerInfo.sites.map((site) => {
        return { value: site.groupId.toString(), label: site.groupName };
      })
    : [];

  const selectedSite = evChargerInfo?.sites.find((site) => site.groupId.toString() === activeSite);

  const PriceList = () => {
    const tariff = selectedSite?.tariff;

    type Price = {
      label: string;
      price: string;
    };

    const renderDayOrNight = (element: EmablerTariffElementUI) => {
      const times = element.timeRestrictions;
      if (times) {
        if (times.startHour === 0 && times.endHour === 24) {
          return '';
        } else if (times.startHour > 15 && times.endHour < 9) {
          return t(`evCharging:common.priceTimeOfUseNight`);
        } else {
          return t(`evCharging:common.priceTimeOfUseDay`);
        }
      }
    };

    const prices: Price[] = [];
    const pricesParking: Price[] = [];

    if (tariff?.priceType === 'Fixed' || tariff?.priceType === 'Spot') {
      const energyElement = tariff?.elements.find((e) => e.priceComponents.find((pc) => pc.type === 'ENERGY'));
      const energySpotElement = tariff?.elements.find((e) => e.priceComponents.find((pc) => pc.type === 'ENERGY_SPOT'));
      const parkingElements = tariff?.elements.filter((e) => e.priceComponents.find((pc) => pc.type === 'PARKING_TIME'));

      const energyPrice = energyElement?.priceComponents.find((pc) => pc.type === 'ENERGY')?.price;
      const energyMargin = energySpotElement?.priceComponents.find((pc) => pc.type === 'ENERGY_SPOT')?.margin;

      if (tariff?.priceType === 'Fixed' && energyPrice) {
        prices.push({
          label: `${formatDays(energyElement.timeRestrictions?.daysOfWeek || [])} ${renderHours(energyElement)}`,
          price: `${setPriceFormat(roundNumber(energyPrice).toString())} €/kWh`,
        });
      }
      if (tariff?.priceType === 'Spot' && energyMargin) {
        prices.push({
          label: t(`evCharging:common.priceSpot`),
          price: `${t(`evCharging:common.margin`)} ${setPriceFormat(roundNumber(energyMargin).toString())} €/kWh`,
        });
      }
      if (parkingElements.length > 0) {
        parkingElements.forEach((parkingElement) => {
          const parkingPrice = parkingElement.priceComponents.find((pc) => pc.type === 'PARKING_TIME')?.price || 0;
          pricesParking.push({
            label: `${formatDays(parkingElement.timeRestrictions?.daysOfWeek || [])} ${renderHours(parkingElement)}`,
            price: `${setPriceFormat(roundNumber(parkingPrice).toString())} €/h`,
          });
        });
      }
    }
    if (tariff?.priceType === 'TimeOfUse') {
      tariff.elements.forEach((element) => {
        const type = element.priceComponents[0].type === 'PARKING_TIME' ? 'parking' : 'energy';
        const array = type === 'parking' ? pricesParking : prices;

        array.push({
          label: `${formatDays(element.timeRestrictions?.daysOfWeek || [])} ${renderDayOrNight(element)} ${renderHours(element)}`,
          price: `${setPriceFormat(roundNumber(element.priceComponents[0].price).toString())} €/${type === 'parking' ? 'h' : 'kWh'}`,
        });
      });
    }

    return (
      <div className={`${styles.detail} ${styles.priceList}`}>
        <h4>{t(`evCharging:common.priceList`)}</h4>
        {prices.length > 0 && (
          <>
            <h5>{t(`evCharging:common.priceListEnergy`)}</h5>
            {prices.map((price) => (
              <div key={price.label} className={styles.price}>
                <span>{price.label}</span>
                <span>{price.price}</span>
              </div>
            ))}
          </>
        )}
        {pricesParking.length > 0 && (
          <>
            <h5>{t(`evCharging:common.priceListParking`)}</h5>
            {pricesParking.map((price) => (
              <div key={price.label} className={styles.price}>
                <span>{price.label}</span>
                <span>{price.price}</span>
              </div>
            ))}
          </>
        )}
        <p className={styles.incTax}>{t(`evCharging:common.pricesIncTax`)}</p>
      </div>
    );
  };

  const start = () => {
    setStartingCharging(true);
    setStartingError(false);
    async function startCharging(): Promise<void> {
      if (selectedMeteringPointContract) {
        const chargerId = selectedCharger.split('-')[0];
        const socketId = selectedCharger.split('-')[1];
        await startEvCharging(selectedMeteringPointContract.customerId, users.activeUser.userId, chargerId, socketId, activeTag);

        setStatusInterval(2000);
      }
    }
    startCharging();
  };

  const stop = () => {
    setStoppingCharging(true);
    async function stopCharging(): Promise<void> {
      if (selectedMeteringPointContract && chargerStatus) {
        const chargerId = selectedCharger.split('-')[0];
        const evseTransactionId =
          chargerStatus?.chargerId === chargerId && chargerStatus?.status !== 'ENDED' && chargerStatus.evseTransactionId !== ''
            ? chargerStatus.evseTransactionId
            : '';

        if (evseTransactionId.length > 0) {
          await stopEvCharging(selectedMeteringPointContract.customerId, users.activeUser.userId, chargerId, evseTransactionId);

          setTimeout(() => {
            const status = getStatus(true);
            if (status?.status === 'ENDED') {
              setStoppingCharging(false);
              setStatusInterval(null);
            }
          }, 2000);
        }
      }
    }
    stopCharging();
  };

  const getStatus = (returnStatus = false): void | EvChargingStatus => {
    async function cancelStart(): Promise<void | EvChargingStatus> {
      if (selectedMeteringPointContract && selectedCharger !== '') {
        const chargerId = selectedCharger.split('-')[0];
        const socketId = selectedCharger.split('-')[1];
        await cancelStartEvCharging(selectedMeteringPointContract.customerId, users.activeUser.userId, chargerId, socketId, activeTag);
      }
    }

    async function fetchChargingStatus(): Promise<void | EvChargingStatus> {
      if (selectedMeteringPointContract && selectedCharger !== '') {
        const chargerId = selectedCharger.split('-')[0];
        const socketId = selectedCharger.split('-')[1];
        const evseTransactionId = chargerStatus?.evseTransactionId ?? 'NA';
        const chargeStatusRequest = await fetchEvChargingStatus(
          selectedMeteringPointContract.customerId,
          users.activeUser.userId,
          chargerId,
          socketId,
          activeTag,
          chargerStatus?.chargerId === chargerId && chargerStatus?.status !== 'ENDED' && evseTransactionId !== '0' ? evseTransactionId : '0',
        );

        dispatchEV(['changeChargerStatus', chargeStatusRequest]);

        if (isCharging(chargeStatusRequest)) {
          setStartingCharging(false);
          setStatusInterval(20000);
        } else {
          setStoppingCharging(false);
        }
        if (
          startingCharging &&
          chargeStatusRequest.status === 'STARTING' &&
          differenceInSeconds(new Date(), new Date(chargeStatusRequest.initTime)) > 30
        ) {
          cancelStart();
          setStartingCharging(false);
          setStartingError(true);
          setStatusInterval(null);
        }
        if (chargeStatusRequest.status === 'ENDED') {
          setStatusInterval(null);
        }
        if (returnStatus) {
          return chargeStatusRequest;
        }
      } else {
        dispatchEV(['changeChargerStatus', undefined]);
        setStartingCharging(false);

        if (returnStatus) {
          return undefined;
        }
      }
    }
    fetchChargingStatus();
  };

  const getChargerInfo = (): void => {
    async function fetchChargerInfo(): Promise<void> {
      const evChargingContract = basicData
        ? basicData.filter((d) => contractIsEvCharging(d)).sort((a, b) => (new Date(a.contractStartDate) < new Date(b.contractStartDate) ? -1 : 1))[0]
        : undefined;

      if (config?.evCharging === true && evChargingContract !== undefined) {
        const chargerInfo = await fetchEVChargerInfo(parseInt(evChargingContract.customerNo), users.activeUser.userId);

        dispatchEV(['changeEvChargerInfo', chargerInfo]);
      }
    }
    fetchChargerInfo();
  };

  React.useEffect(() => {
    setStartingError(false);
    setChargingError(false);
    getStatus();
  }, [selectedCharger]);

  useInterval(() => {
    getStatus();
  }, statusInterval);

  useInterval(() => {
    getChargerInfo();
  }, 10000);

  React.useEffect(() => {
    const site = evChargerInfo?.sites.find((s) => s.groupId === parseInt(activeSite));
    const charger = site?.chargers.find((c) => c.chargerId === selectedCharger.split('-')[0]);
    if (!charger) {
      if (site && site.chargers.length > 0) {
        dispatchEV(['changeSelectedCharger', `${site.chargers[0].chargerId}-${site.chargers[0].sockets[0].id}`]);
      } else {
        dispatchEV(['changeSelectedCharger', '']);
      }
    }
  }, [activeSite]);

  const activeSocket = selectedSite
    ? selectedSite.chargers
        .find((c) => c.chargerId === selectedCharger.split('-')[0])
        ?.sockets.find((s) => s.id.toString() === selectedCharger.split('-')[1])
    : undefined;

  return (
    <div>
      <header className={`${styles.evChargingHeader} ${styles.mainHeader}`}>
        <h1 aria-live="polite">{t(`evCharging:common.title`)}</h1>
        <Select options={tags} activeOption={activeTag} setActiveOption={setTempActiveTag} />
      </header>

      {(loadingBasicData || evChargerInfoLoading) && <Loading />}
      {!evChargerInfo && !loadingBasicData && !evChargerInfoLoading && <Box>{t(`evCharging:common.noEvContract`)}</Box>}
      {evChargerInfo && (
        <Box wrapper size="double">
          <Box wrapper size="twoThirds">
            <Box className={`${styles.noGrow}`}>
              <header className={styles.evChargingHeader}>
                <h2>{t(`evCharging:common.site`)}</h2>
                <div>
                  <Select options={sites} activeOption={activeSite} setActiveOption={setTempActiveSite} />
                </div>
              </header>
              {sites.length === 0 && <p>{t(`evCharging:common.noSitesAvailable`)}</p>}
              {selectedSite && (
                <>
                  {selectedSite.chargers.length > 0 && <ChargerDropdown site={selectedSite} />}
                  {chargerStatus && isCharging(chargerStatus) && (
                    <>
                      <Battery startTime={chargerStatus.initTime} serverTime={chargerStatus.serverTime} />
                      {chargerStatus.consumptionWh !== '-1' && parseInt(chargerStatus.consumptionWh) > -1 && (
                        <div className={styles.detail}>
                          <h4>{t(`evCharging:common.consumption`)}</h4>
                          {formatWatts(parseInt(chargerStatus.consumptionWh))}h
                        </div>
                      )}
                      {chargerStatus.currentPower !== '-1' && chargerStatus.currentPower > -1 && (
                        <div className={styles.detail}>
                          <h4>{t(`evCharging:common.consumptionPower`)}</h4>
                          {formatWatts(chargerStatus.currentPower)}
                        </div>
                      )}
                    </>
                  )}

                  {chargerStatus && isCharging(chargerStatus) && activeSocket && activeSocket.maxWatts && (
                    <div className={`${styles.detail} ${styles.priceList}`}>
                      <h4>{t(`evCharging:common.type`)}</h4>
                      {formatWatts(activeSocket.maxWatts)}, {activeSocket.type}
                    </div>
                  )}
                  <PriceList />
                  {startingError && (
                    <Snackbar type="error" inline>
                      {t(`evCharging:charging.failedToStart`)}
                    </Snackbar>
                  )}
                  {chargingError && (
                    <Snackbar type="error" inline>
                      {t(`evCharging:charging.chargingError`)}
                    </Snackbar>
                  )}
                  {(chargerStatus?.status === 'STARTED' || chargerStatus?.status === 'CHARGING') && chargerStatus?.isSuspended === true && (
                    <Snackbar type="info" inline>
                      {t(`evCharging:charging.chargingSuspended`)}
                    </Snackbar>
                  )}
                  {(startingCharging || stoppingCharging) && <Loading size="small" />}
                  {chargerStatus?.status === 'ENDED' && differenceInSeconds(new Date(), new Date(chargerStatus.updatedTime)) < 3600 && (
                    <Snackbar type="success" inline>
                      <Trans i18nKey="evCharging:charging.chargingFinished">
                        . <Link to={`/evCharging/history`}></Link>
                      </Trans>
                    </Snackbar>
                  )}
                  {startingCharging && (
                    <Button className="buttonCharge_starting" type="button">
                      {t(`evCharging:charging.startingCharging`)}
                    </Button>
                  )}
                  {stoppingCharging && (
                    <Button className="buttonCharge_stopping" type="button">
                      {t(`evCharging:charging.stoppingCharging`)}
                    </Button>
                  )}
                  {selectedSite.chargers.length === 0 && (
                    <Button className="buttonCharge_disabled" type="button">
                      {t(`evCharging:charging.noAvailableChargers`)}
                    </Button>
                  )}
                  {selectedSite.chargers.length > 0 && (
                    <>
                      {(!chargerStatus ||
                        (chargerStatus && chargerStatus.status !== 'STARTED' && chargerStatus.status !== 'CHARGING' && !startingCharging)) && (
                        <Button className="buttonCharge_start" type="button" onClick={start}>
                          {t(`evCharging:charging.startCharging`)}
                        </Button>
                      )}
                      {chargerStatus && isCharging(chargerStatus) && !stoppingCharging && (
                        <Button className="buttonCharge_stop" type="button" onClick={stop}>
                          {t(`evCharging:charging.finishCharging`)}
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
          <Box size="third" noStyles>
            <Liftup className="link" link="/evCharging/history">
              <h4>{t(`evCharging:sidebar.history`)}</h4>
            </Liftup>
            <Liftup className="link" link="/evCharging/contractsAndTags">
              <h4>{t(`evCharging:sidebar.contractsAndTags`)}</h4>
            </Liftup>
            <Liftup className="link" link="/evCharging/reportIssue">
              <h4>{t(`evCharging:sidebar.reportIssue`)}</h4>
            </Liftup>
            <Liftup className="linkExternal" link="https://www.lumme-energia.fi/yrityksille/sahkoauton-latauspalvelut#ukk">
              <h4>{t(`evCharging:sidebar.faq`)}</h4>
            </Liftup>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default EvCharging;
